/* eslint-disable @typescript-eslint/indent */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DateQuestion } from "../../../../shared/components/dynamic-form/question-date";
import { DropdownOption, DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { NumberQuestion } from "../../../../shared/components/dynamic-form/question-number";
import { TextAreaQuestion } from "../../../../shared/components/dynamic-form/question-textarea";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { pattern } from "../../../../shared/constants/patterns";
import { VALUE } from "../../../../shared/constants/values";
import { UserInfo } from "../../../../shared/types/user-info";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { isSameOrAfter } from "../../../../shared/validators/validators";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { MaintenanceProviderService } from "../../maintenance-provider/maintenance-provider.service";
import { MaintenanceProvider } from "../../maintenance-provider/maintenance-provider.types";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { contractStatuses, contractTypes } from "../contract";
import { Contract } from "../contract.type";

@Injectable()
export class CreateContractService {
  constructor(
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private maintenanceProviderService: MaintenanceProviderService,
  ) {}

  getMaintenanceProviders(
    query: { name?: string; partnerId?: string },
    currentUser: UserInfo,
  ): Observable<DropdownOption[]> {
    const partnerId = query?.partnerId || currentUser?.partnerId || currentUser?.company?.partnerId;
    return this.maintenanceProviderService
      .getMaintenanceProviders({
        relations: { partners: true },
        joinType: "LEFT",
        ...(currentUser.isGlobal() && {
          where: [
            {
              isDefault: true,
              name: { $cont: query?.name },
            },
            {
              partners: { id: partnerId },
              name: { $cont: query?.name },
            },
          ],
        }),
        // if user is not global, then it only can retrieve non channelworks maintenance providers
        ...(!currentUser.isGlobal() && {
          where: [
            {
              isChannelWorks: false,
              name: { $cont: query?.name },
            },
          ],
        }),
      })
      .pipe(
        map((data: PaginatedResponse<MaintenanceProvider>) =>
          data.items.map((c: MaintenanceProvider) => ({ value: c.id, label: c.name })),
        ),
      );
  }

  getPartner(partnerId: string): Observable<DropdownOption[]> {
    return this.partnerService.getPartner(partnerId).pipe(map((r: Partner) => [{ value: r.id, label: r.name }]));
  }

  getPartners(query?: { name: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ value: c.id, label: c.name }))));
  }

  getCompanies(query: { partnerId: string; name?: string }): Observable<DropdownOption[]> {
    const { partnerId, ...filters } = query;
    return this.partnerService
      .getCompaniesByQuery(partnerId, { where: setFilterCondition(filters, "$cont") })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ value: c.id, label: c.name }))));
  }

  getAllCompanies(query?: { name?: string }): Observable<DropdownOption[]> {
    return this.companyService
      .getCompanies({ where: setFilterCondition(query, "$cont") })
      .pipe(
        map((data: PaginatedResponse<Company>) => data.items.map((c: Company) => ({ value: c.id, label: c.name }))),
      );
  }

  createDateQuestion(options?: {
    contract?: Contract;
    onlyReseller?: boolean;
    statusValue?: string;
    startDateSelected?: string;
    isUpdating?: boolean;
  }): DateQuestion {
    const value = options.contract?.startDate || options?.startDateSelected;
    const errors = [
      {
        key: "minDate",
        validator: isSameOrAfter(VALUE.DEFAULT_MIN_DATE),
        message: "Date can't be lower than 1/1/2000",
      },
    ];
    const dateQuestion = new DateQuestion({
      value,
      key: "startDate",
      label: "Start Date",
      required: true,
      minDate: moment(VALUE.DEFAULT_MIN_DATE).utc().toDate(),
      disabled: options.onlyReseller,
      errors,
      copyToClipboard: options?.isUpdating,
      order: 6,
    });

    return dateQuestion;
  }

  generateQuestions(options: {
    contract?: Contract;
    partnerValue?: string;
    companyValue?: string;
    companyOptions?: DropdownOption[];
    companyEdit?: boolean;
    companySkip?: boolean;
    partnerSkip?: boolean;
    currentUser?: UserInfo;
    statusValue?: string;
    editMode?: boolean;
    isManageView?: boolean;
    skipReseller?: boolean;
    hideReseller?: boolean;
    showContractNumber?: boolean;
    onlyReseller?: boolean;
    maintenanceProviderValue?: MaintenanceProvider;
    version?: number;
    skipMaintenanceProvider?: boolean;
    partnerDisabled?: boolean;
    operation?: Operation;
  }): Observable<QuestionBase<string | boolean | number>[]> {
    if (options?.showContractNumber === null) {
      options.showContractNumber = false;
    }
    options = { companyEdit: true, editMode: false, ...options };
    const isUpdating = options.operation === Operation.update;
    const questions: QuestionBase<string | boolean | number>[] = [
      !options.partnerSkip &&
        new DropdownQuestion({
          value: options.partnerValue,
          key: "partnerId",
          label: "Partner",
          ignoreInSubmit: true,
          required: true,
          tooltipEnabled: true,
          currentOption: {
            label: options.contract?.company?.partner?.name,
            value: options.contract?.company?.partner?.id,
          },
          options: options?.companyOptions,
          asyncOptions: options.partnerValue ? this.getPartner(options.partnerValue) : this.getPartners(),
          disabled: options.onlyReseller || options.partnerDisabled,
          search: {
            filter: {
              callback: ({ value: name }) => this.getPartners({ name }),
            },
          },
          copyToClipboard: isUpdating,
          order: 1,
        }),

      !options?.companySkip &&
        new DropdownQuestion({
          value: options.companyValue || options.contract?.companyId,
          key: "companyId",
          label: "Company",
          tooltipEnabled: true,
          required: true,
          disabled: !options?.companyEdit,
          currentOption: {
            label: options.contract?.company?.name,
            value: options.contract?.company?.id,
          },
          options: options?.companyOptions,
          ignoreInSubmit: options.currentUser.isCompany(),
          asyncOptions: options?.partnerValue
            ? this.getCompanies({
                partnerId: options?.partnerValue,
              })
            : undefined,
          dependsOn: {
            key: "partnerId",
            callback: ({ parentValue: partnerId }) => this.getCompanies({ partnerId }),
          },
          search: {
            filter: {
              parentKey: "partnerId",
              callback: ({ parentValue: partnerId, value: name }) =>
                this.getCompanies({
                  partnerId: options?.partnerValue || partnerId,
                  name,
                }),
            },
          },
          order: 2,
        }),

      options.editMode &&
        new DropdownQuestion({
          value: options.companyValue || options.contract?.companyId,
          key: "companyId",
          tooltipEnabled: true,
          label: "Company",
          required: false,
          disabled: true,
          currentOption: {
            label: options.contract?.company?.name,
            value: options.contract?.company?.id,
          },
          options: options?.companyOptions,
          asyncOptions: this.getAllCompanies(),
          search: {
            filter: {
              callback: ({ value: name }) => this.getAllCompanies({ name: name }),
            },
          },
          copyToClipboard: isUpdating,
          order: 3,
        }),
      new TextboxQuestion({
        value: options.contract?.name,
        key: "name",
        label: "Name",
        required: true,
        copyToClipboard: isUpdating,
        order: 4,
        disabled: options.onlyReseller,
        tooltipEnabled: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(2),
            message: "Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(200),
            message: "Name is <strong>too long</strong>",
          },
        ],
      }),
      options?.showContractNumber &&
        new TextboxQuestion({
          value: options?.contract?.number + "-" + options?.version,
          key: "number",
          label: "Number",
          disabled: true,
          copyToClipboard: isUpdating,
          order: 5,
        }),

      this.createDateQuestion({
        contract: options?.contract,
        onlyReseller: options?.onlyReseller,
        statusValue: options?.statusValue,
        isUpdating: isUpdating,
      }),

      new DateQuestion({
        value: options.contract?.endDate,
        key: "endDate",
        label: "End Date",
        required: true,
        disabled: options.onlyReseller,
        ...(!options.isManageView && {
          dependsOn: {
            key: "startDate",
          },
        }),
        copyToClipboard: isUpdating,
        order: 7,
      }),

      this.generateContractStatusQuestion(
        "status",
        "Status",
        8,
        options.contract?.startDate,
        options.contract?.endDate,
        options?.statusValue,
        isUpdating,
      ),

      new DropdownQuestion({
        value: options.contract?.type,
        key: "type",
        label: "Type",
        options: contractTypes,
        required: true,
        disabled: options.onlyReseller,
        copyToClipboard: isUpdating,
        order: 9,
      }),

      !options.skipMaintenanceProvider &&
        new DropdownQuestion({
          value: options.contract?.maintenanceProviderId,
          key: "maintenanceProviderId",
          label: "Maintenance Provider",
          currentOption: {
            label: options.contract?.maintenanceProvider?.name,
            value: options.contract?.maintenanceProvider?.id,
          },
          disabled: options.onlyReseller,
          required: true,
          tooltipEnabled: true,
          asyncOptions: this.getMaintenanceProviders({}, options.currentUser),
          dependsOn: {
            key: "partnerId",
            callback: ({ parentValue: partnerId }) => {
              return this.getMaintenanceProviders({ partnerId }, options.currentUser);
            },
          },
          search: {
            filter: {
              parentKey: "partnerId",
              callback: ({ value: name, parentValue: partnerId }) => {
                return this.getMaintenanceProviders({ name, partnerId }, options.currentUser);
              },
            },
          },
          copyToClipboard: isUpdating,
          order: 10,
        }),

      !options.skipReseller &&
        new TextboxQuestion({
          value: options.contract?.reseller,
          key: "reseller",
          label: "Reseller",
          required: false,
          hide: options.hideReseller,
          copyToClipboard: isUpdating,
          order: 12,
        }),

      options.editMode &&
        !options.currentUser?.isSalesRepUser() &&
        this.generateTotalPriceQuestion(
          "totalChannelWorksCost",
          "Total CW Cost",
          13,
          options?.currentUser,
          options?.contract,
          isUpdating,
        ),

      this.generateTotalPriceQuestion(
        "totalPartnerPrice",
        "Total Partner Price",
        options.hideReseller && options.currentUser.isPartner() ? 14 : 15,
        options?.currentUser,
        options?.contract,
        isUpdating,
      ),

      options.currentUser.isGlobal() &&
        new TextboxQuestion({
          value: options.contract?.sfOpportunity,
          key: "sfOpportunity",
          label: "SalesForce Opportunity",
          tooltipEnabled: true,
          disabled: options.onlyReseller,
          errors: [
            {
              key: "minlength",
              validator: Validators.minLength(1),
              message: "SalesForce Opportunity is <strong>too short</strong>",
            },
            {
              key: "maxlength",
              validator: Validators.maxLength(600),
              message: "SalesForce Opportunity is <strong>too long</strong>",
            },
          ],
          copyToClipboard: isUpdating,
          order: 17,
        }),

      options.currentUser.isGlobal() &&
        new TextAreaQuestion({
          value: options.contract?.notes,
          key: "notes",
          label: "Internal Notes",
          disabled: options.onlyReseller,
          errors: [
            {
              key: "pattern",
              validator: Validators.pattern(pattern.words),
              message: "Internal notes field is <strong>not valid</strong>",
            },
            {
              key: "minlength",
              validator: Validators.minLength(1),
              message: "Internal notes are <strong>too short</strong>",
            },
            {
              key: "maxlength",
              validator: Validators.maxLength(1000),
              message: "Internal notes are <strong>too long</strong>",
            },
          ],
          minHeight: 200,
          copyToClipboard: isUpdating,
          order: 18,
        }),
      options.currentUser.isGlobal() &&
        new TextAreaQuestion({
          value: options.contract?.configurationNotes,
          key: "configurationNotes",
          label: "Configuration Notes",
          disabled: options.onlyReseller,
          errors: [
            {
              key: "pattern",
              validator: Validators.pattern(pattern.words),
              message: "Configuration notes field is <strong>not valid</strong>",
            },
            {
              key: "minlength",
              validator: Validators.minLength(1),
              message: "Configuration notes are <strong>too short</strong>",
            },
            {
              key: "maxlength",
              validator: Validators.maxLength(1000),
              message: "Configuration notes are <strong>too long</strong>",
            },
          ],
          minHeight: 200,
          copyToClipboard: isUpdating,
          order: 19,
        }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  generateTotalPriceQuestion(
    key: keyof Pick<Contract, "totalPartnerPrice" | "totalChannelWorksCost">,
    label: string,
    order: number,
    user: UserInfo,
    contract?: Contract,
    isUpdating?: boolean,
  ): NumberQuestion {
    const question = new NumberQuestion({
      value: `${contract?.[key] || 0}`,
      key,
      type: "text",
      label,
      placeholder: "0",
      required: false,
      hide: user.isCompany() || !contract,
      disabled: true,
      isCurrency: true,
      copyToClipboard: isUpdating,
      order,
    });
    return question;
  }

  generateContractStatusQuestion(
    key: keyof Pick<Contract, "status">,
    label: string,
    order: number,
    startDate: string,
    endDate: string,
    statusValue?: string,
    isUpdating?: boolean,
  ): TextboxQuestion {
    const question = new TextboxQuestion({
      value: statusValue
        ? contractStatuses.find((status) => status.value === statusValue)?.label
        : this.calculateContractStatus(startDate, endDate),
      key,
      type: "text",
      label,
      ignoreInSubmit: true,
      required: false,
      disabled: true,
      copyToClipboard: isUpdating,
      order,
    });
    return question;
  }

  calculateContractStatus(startDate: string, endDate: string): string | null {
    if (!startDate || !endDate) return;

    const today = moment().utc().startOf("day");
    const _startDate = moment(startDate).utc().startOf("day");
    const _endDate = moment(endDate).utc().startOf("day");

    /*Edit mode doesn't remove endDate after changing startDate*/
    if (_startDate.isAfter(_endDate)) return;

    const isActive = _startDate.isSameOrBefore(today) && _endDate.isSameOrAfter(today);
    const isPending = _startDate.isAfter(today);
    const isExpired = _endDate.isBefore(today);

    if (isActive) return contractStatuses.find((status) => status.value === "active")?.label;
    else if (isPending) return contractStatuses.find((status) => status.value === "pending")?.label;
    else if (isExpired) return contractStatuses.find((status) => status.value === "expired")?.label;
  }
}
